import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { SessionView } from "../../lib/types";

type Tone = "green" | "amber" | "red" | "purple" | "gray";
const TONES: Record<Tone, string> = {
  green: "bg-emerald-100 text-emerald-800 border-emerald-300",
  amber: "bg-amber-100 text-amber-900 border-amber-300",
  red: "bg-red-100 text-red-700 border-red-300",
  purple: "bg-purple-100 text-cosmic-purple border-purple-300",
  gray: "bg-gray-100 text-gray-700 border-gray-300",
};

export function Pill({ tone, children, pulse }: { tone: Tone; children: ReactNode; pulse?: boolean }) {
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-black ${TONES[tone]} ${pulse ? "animate-pulse" : ""}`}>{children}</span>;
}

export function SessionStatusBadge({ status }: { status: SessionView["status"] }) {
  const { t } = useTranslation();
  const tone: Tone = status === "active" ? "green" : status === "expiring" ? "amber" : status === "overdue" ? "red" : "gray";
  return <Pill tone={tone} pulse={status === "overdue"}>{t(`status.${status}`)}</Pill>;
}

export function SectionHeader({ icon, title, subtitle, right }: { icon?: string; title: string; subtitle?: string; right?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-3">
      <div className="flex items-center gap-2">
        {icon && <span className="text-base" aria-hidden>{icon}</span>}
        <div>
          <h2 className="text-sm font-black uppercase tracking-wide text-cosmic-navy">{title}</h2>
          {subtitle && <p className="text-[10px] font-medium text-gray-500">{subtitle}</p>}
        </div>
      </div>
      {right}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="rounded-2xl border border-dashed border-gray-200 py-8 text-center text-xs font-bold text-gray-400">{children}</div>;
}

export function Avatar({ text, tone = "purple" }: { text: string; tone?: "purple" | "amber" | "red" | "green" }) {
  const map = { purple: "bg-purple-100 text-cosmic-purple", amber: "bg-amber-100 text-amber-900", red: "bg-red-100 text-red-600", green: "bg-emerald-100 text-emerald-800" };
  return <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-xs font-black ${map[tone]}`}>{text}</div>;
}
